import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import { FiSearch } from "react-icons/fi";
import { List, Clock, CheckCircle, XCircle, Ban, Calendar, X, Trash2 } from "lucide-react";
import TableComponent from "../../components/Deals/TableComponent";
import useDeals from "../../hooks/useDeals";
import SenderIdShimmerComponent from "../../components/Senders/SenderIdShimmerComponent";
import LoadErrorPlaceholder from "../../components/LoadErrorPlaceholder";
import DealStatCard from "../../components/Deals/DealStatCard";

const HEADER_TO_SORT_BY = {
  id: "id",
  title: "title",
  company: "company_name",
  offer_type: "offer_type",
  pricing: "price",
  dateCreated: "created_at",
  expiryDate: "expiry_date",
  status: "status",
};

const Deals = () => {
  const {
    deals,
    loading,
    error,
    stats,
    pagination,
    filters,
    debouncedSearch,
    changePage,
    changeItemsPerPage,
    updateStatusFilter,
    updateDateFilter,
    clearDateFilter,
    updateSort,
    refreshData,
  } = useDeals();

  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateDropdownPosition, setDateDropdownPosition] = useState({ top: 0, left: 0 });
  const dateFilterTriggerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showDateFilter &&
        !event.target.closest(".date-filter-container") &&
        !event.target.closest(".date-filter-dropdown-portal")
      ) {
        setShowDateFilter(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDateFilter]);

  const updateDateDropdownPosition = useCallback(() => {
    if (!dateFilterTriggerRef.current) return;
    const rect = dateFilterTriggerRef.current.getBoundingClientRect();
    const dropdownWidth = Math.min(320, window.innerWidth - 24);
    let left = rect.left;
    if (left + dropdownWidth > window.innerWidth - 12) {
      left = window.innerWidth - dropdownWidth - 12;
    }
    setDateDropdownPosition({
      top: rect.bottom + 8,
      left,
    });
  }, []);

  useLayoutEffect(() => {
    if (!showDateFilter) return;
    updateDateDropdownPosition();
  }, [showDateFilter, updateDateDropdownPosition]);

  useEffect(() => {
    if (!showDateFilter) return;
    window.addEventListener("scroll", updateDateDropdownPosition, true);
    window.addEventListener("resize", updateDateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDateDropdownPosition, true);
      window.removeEventListener("resize", updateDateDropdownPosition);
    };
  }, [showDateFilter, updateDateDropdownPosition]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  const handleSort = useCallback(
    (headerKey) => {
      const sortBy = HEADER_TO_SORT_BY[headerKey] || "created_at";
      const nextOrder =
        filters.sortBy === sortBy && filters.sortOrder === "desc" ? "asc" : "desc";
      updateSort(sortBy, nextOrder);
    },
    [filters.sortBy, filters.sortOrder, updateSort]
  );

  const sortConfig = React.useMemo(
    () => ({
      key: Object.entries(HEADER_TO_SORT_BY).find(([, v]) => v === filters.sortBy)?.[0] ?? null,
      direction: filters.sortOrder === "asc" ? "ascending" : "descending",
    }),
    [filters.sortBy, filters.sortOrder]
  );

  const handleSelectedRows = useCallback(() => {}, []);

  const serverPagination = {
    currentPage: pagination.currentPage,
    totalPages: pagination.totalPages,
    itemsPerPage: pagination.itemsPerPage,
    totalItems: pagination.totalItems,
  };

  return (
    <div>
      <Navbar page="Deals" />
      <div className="p-3 min-h-screen bg-gray-50 sm:p-4 md:p-6">
        {loading && deals.length === 0 ? (
          <SenderIdShimmerComponent />
        ) : error && deals.length === 0 ? (
          <LoadErrorPlaceholder />
        ) : (
          <>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              className="space-y-6"
            >
              {/* Stats Cards */}
              <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                <DealStatCard
                  title="Pending Approval"
                  subtitle="Deals awaiting review"
                  count={stats.pending}
                  change="5"
                  trend="up"
                  color="amber"
                  icon=<svg
                    className="w-8 h-8"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 8V12L14.5 14.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M5.63605 18.364C2.12132 14.8492 2.12132 9.15076 5.63605 5.63604C9.15076 2.12132 14.8492 2.12132 18.364 5.63604C21.8787 9.15076 21.8787 14.8492 18.364 18.364C14.8492 21.8787 9.15076 21.8787 5.63605 18.364Z"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                  itemVariants={itemVariants}
                />

                <DealStatCard
                  title="Approved Deals"
                  subtitle="Currently live offers"
                  count={stats.approved}
                  change="5"
                  trend="up"
                  color="emerald"
                  icon=<svg
                    className="w-8 h-8"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13L9 17L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  itemVariants={itemVariants}
                />
                <DealStatCard
                  title="Declined Deals"
                  subtitle="Rejected proposals"
                  count={stats.rejected}
                  change="-2"
                  trend="down"
                  color="rose"
                  icon=<svg
                    className="w-8 h-8"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 18L18 6M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  itemVariants={itemVariants}
                />
              </div>

              {/* Table Section */}
              <motion.div
                variants={itemVariants}
                className="bg-white rounded-xl shadow-sm"
              >
                {/* Trash Bin quick-link */}
                <div className="flex justify-end px-3 pt-3 sm:px-4 sm:pt-4">
                  <Link
                    to="/deals/trash"
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-[#FF6C6C]/30 bg-[#FF6C6C]/8 text-[#CC2E2E] text-xs font-semibold hover:bg-[#FF6C6C]/15 hover:border-[#FF6C6C]/50 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    View Trash Bin
                  </Link>
                </div>

                {/* Table Controls - responsive: stacked on mobile, row on larger screens */}
                <div className="flex flex-col gap-3 p-3 border-b sm:flex-row sm:flex-wrap sm:gap-4 sm:justify-between sm:items-center sm:p-4 min-w-0 overflow-x-hidden">
                  <div className="relative w-full min-w-0 sm:w-auto sm:min-w-[200px] sm:max-w-xs">
                    <FiSearch className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search deals..."
                      defaultValue={filters.search}
                      className="py-2 pr-4 pl-10 w-full text-base rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      onChange={(e) => debouncedSearch(e.target.value)}
                    />
                  </div>
                  <div className="relative w-full min-w-0 date-filter-container sm:w-auto sm:shrink-0">
                    <button
                      ref={dateFilterTriggerRef}
                      type="button"
                      onClick={() => setShowDateFilter(!showDateFilter)}
                      className={`flex items-center justify-center gap-2 px-3 py-2 sm:px-4 rounded-lg border transition-all duration-200 w-full sm:w-auto text-xs sm:text-sm font-medium ${
                        filters.dateFrom || filters.dateTo
                          ? "border-[#FF6C6C] bg-[#FF6C6C]/5 text-[#FF6C6C]"
                          : "border-gray-300 text-gray-700 hover:border-[#FF6C6C] hover:bg-gray-50"
                      }`}
                    >
                      <Calendar className="w-4 h-4 shrink-0" />
                      <span className="whitespace-nowrap">
                        {filters.dateFrom || filters.dateTo ? "Date Filtered" : "Filter by Date"}
                      </span>
                      {(filters.dateFrom || filters.dateTo) && (
                        <X
                          className="w-4 h-4 ml-1 shrink-0 hover:bg-[#FF6C6C]/10 rounded-full p-0.5"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearDateFilter();
                          }}
                        />
                      )}
                    </button>
                    {showDateFilter &&
                      createPortal(
                        <div
                          className="date-filter-dropdown-portal fixed z-[9999] w-[min(20rem,calc(100vw-2rem))] min-w-0 bg-white rounded-xl border border-gray-200 shadow-xl p-4"
                          style={{
                            top: dateDropdownPosition.top,
                            left: dateDropdownPosition.left,
                          }}
                        >
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-sm font-semibold text-gray-900">
                              Filter by Date Range
                            </h3>
                            <button
                              type="button"
                              onClick={() => setShowDateFilter(false)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="space-y-4">
                            <div>
                              <label htmlFor="deals-dateFrom" className="block mb-2 text-sm font-medium text-gray-700">
                                From Date
                              </label>
                              <input
                                type="date"
                                id="deals-dateFrom"
                                max={filters.dateTo || undefined}
                                defaultValue={filters.dateFrom || ""}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] outline-none text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="deals-dateTo" className="block mb-2 text-sm font-medium text-gray-700">
                                To Date
                              </label>
                              <input
                                type="date"
                                id="deals-dateTo"
                                min={filters.dateFrom || undefined}
                                defaultValue={filters.dateTo || ""}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] outline-none text-sm"
                              />
                            </div>
                          </div>
                          <div className="flex justify-between items-center pt-4 mt-6 border-t border-gray-200">
                            <button
                              type="button"
                              onClick={() => {
                                clearDateFilter();
                                setShowDateFilter(false);
                              }}
                              className="text-sm text-gray-600 hover:text-gray-800"
                            >
                              Clear
                            </button>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => setShowDateFilter(false)}
                                className="px-4 py-2 text-sm text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const dateFrom = document.getElementById("deals-dateFrom")?.value ?? "";
                                  const dateTo = document.getElementById("deals-dateTo")?.value ?? "";
                                  updateDateFilter(dateFrom, dateTo);
                                  setShowDateFilter(false);
                                }}
                                className="px-4 py-2 text-sm text-white bg-[#FF6C6C] rounded-lg hover:bg-[#FF6C6C]/90"
                              >
                                Apply Filter
                              </button>
                            </div>
                          </div>
                        </div>,
                        document.body
                      )}
                  </div>
                  <div className="flex flex-wrap gap-2 items-center min-w-0">
                    <button
                      onClick={() => updateStatusFilter("all")}
                      className={`flex items-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-colors shrink-0 ${
                        filters.status === "all"
                          ? "bg-[#FF6C6C] text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      <List className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                      <span className="whitespace-nowrap">All Deals</span>
                      <span
                        className={`ml-1.5 min-w-[1.25rem] inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
                          filters.status === "all" ? "bg-white/20 text-white" : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {stats.total ?? 0}
                      </span>
                    </button>
                    <button
                      onClick={() => updateStatusFilter("Pending")}
                      className={`flex items-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-colors shrink-0 ${
                        filters.status === "Pending"
                          ? "bg-amber-500 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                      <span className="whitespace-nowrap">Pending</span>
                      <span
                        className={`ml-1.5 min-w-[1.25rem] inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
                          filters.status === "Pending" ? "bg-white/20 text-white" : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {stats.pending ?? 0}
                      </span>
                    </button>
                    <button
                      onClick={() => updateStatusFilter("Approved")}
                      className={`flex items-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-colors shrink-0 ${
                        filters.status === "Approved"
                          ? "bg-emerald-500 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                      <span className="whitespace-nowrap">Active</span>
                      <span
                        className={`ml-1.5 min-w-[1.25rem] inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
                          filters.status === "Approved" ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-700"
                        }`}
                      >
                        {stats.approved ?? 0}
                      </span>
                    </button>
                    <button
                      onClick={() => updateStatusFilter("expired")}
                      className={`flex items-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-colors shrink-0 ${
                        filters.status === "expired"
                          ? "bg-red-500 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      <XCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                      <span className="whitespace-nowrap">Expired</span>
                    </button>
                    <button
                      onClick={() => updateStatusFilter("Rejected")}
                      className={`flex items-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-colors shrink-0 ${
                        filters.status === "Rejected"
                          ? "bg-rose-500 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      <Ban className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                      <span className="whitespace-nowrap">Rejected</span>
                      <span
                        className={`ml-1.5 min-w-[1.25rem] inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
                          filters.status === "Rejected" ? "bg-white/20 text-white" : "bg-rose-100 text-rose-700"
                        }`}
                      >
                        {stats.rejected ?? 0}
                      </span>
                    </button>
                  </div>
                </div>
                {/* Hidden for now */}
                {/* <div className="flex gap-3">
                    <button
                      className={`flex items-center gap-2 px-4 py-2 border rounded-lg ${
                        selectedRows.length > 0
                          ? "bg-red-50 text-red-600 hover:bg-red-100 border-red-200"
                          : "bg-gray-50 text-gray-300 cursor-not-allowed"
                      }`}
                      disabled={selectedRows.length === 0}
                      onClick={() =>
                        console.log("Delete selected:", selectedRows)
                      }
                    >
                      Delete Selected
                    </button>
                    <button className="flex gap-2 items-center px-4 py-2 rounded-lg border hover:bg-gray-50">
                      <FiDownload /> Export
                    </button>
                  </div> */}

                {/* Table Component - server-side pagination/filtering */}
                <TableComponent
                  data={deals}
                  searchTerm={filters.search}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  refreshData={refreshData}
                  onSelectedRowsChange={handleSelectedRows}
                  statusFilter={filters.status}
                  serverPagination={serverPagination}
                  onPageChange={changePage}
                  onItemsPerPageChange={changeItemsPerPage}
                  isLoading={loading}
                  onClearFilters={() => {
                    updateStatusFilter("all");
                    clearDateFilter();
                  }}
                />
              </motion.div>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
};

export default Deals;
